import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const HERO_MOCKUP =
  "https://customer-assets.emergentagent.com/job_zero-signals-landing/artifacts/nfpnsz26_Progetto%20senza%20titolo%20%287%29.png";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/fb14abd8-25b7-415a-8776-6e6444cdf823/images/996c398f6deb5ce565f346272cf36643ae1b18d21c72127b83f97dae30fce1f1.png";

export default function Hero() {
  return (
    <section
  id="top"
  data-testid="hero-section"
  className="relative flex min-h-screen items-center overflow-hidden pt-20 lg:pt-20"
>
      {/* Background image overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-screen"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-brand-black" />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:gap-10 lg:px-10">
        {/* LEFT: TEXT */}
        <div className="lg:col-span-7">
          <motion.div
  initial={{ opacity: 0, y: 14 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7 }}
  className="mb-6 inline-flex items-center gap-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs uppercase tracking-[0.22em] text-white/70 backdrop-blur"
  data-testid="hero-pill"
>
  <img
    src="https://customer-assets-0z36b82j.emergentagent.net/job_zero-signals-landing/artifacts/jmp0fm2e_Logo%20FAMI%20-%20Logo%20ZS%20-%20Zeta%20%285%29.png"
    alt="ZeroSignals mark"
    className="h-7 w-auto object-contain"
  />

  <span className="-translate-x">
    ZeroSignals · Analizza come un professionista
  </span>
</motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            data-testid="hero-headline"
            className="font-display text-5xl font-black leading-[1.05] tracking-tighter md:text-6xl lg:text-7xl"
          >
            <span className="text-white">Una nuova era</span>
            <br />
            <span className="text-gradient-gold">Zero</span>{""}
            <span className="relative inline-block text-white">
              <span className="text-glow-blue">Signals</span>
              <span className="absolute -bottom-2 left-0 h-[3px] w-3/4 bg-gradient-to-r from-brand-neon-blue via-brand-gold to-transparent" />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18 }}
            data-testid="hero-subheadline"
            className="mt-7 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg"
          >
            ZeroSignals analizza una partita partendo da quote, statistiche e dati. Elabora le informazioni e le trasforma in probabilità, indicatori e segnali facili da leggere. Meno tempo a cercare. Più tempo ad analizzare.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#come-funziona"
              data-testid="hero-cta-primary"
              className="group relative inline-flex items-center gap-2 rounded-full border-2 border-brand-neon-blue bg-black px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] text-brand-gold transition-all hover:bg-brand-gold hover:text-black hover:shadow-[0_0_45px_-5px_rgba(0,240,255,0.8)]"
            >
              Scopri come funziona
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="#the-hub"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3.5 text-sm font-medium text-white/80 backdrop-blur transition-colors hover:text-white"
            >
              Entra in ZeroSignals
              <ArrowRight size={14} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            data-testid="hero-stats"
            className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-white/5 pt-8"
          >
            {[
              { v: "250+", l: "Membri Attivi" },
              { v: "7500+", l: "Analisi Backend" },
              { v: "24/7", l: "Community Hub" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-2xl font-bold text-gradient-gold">
                  {s.v}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-white/45">
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* RIGHT: MOCKUP WITH ZOOM EFFECT */}
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="relative mx-auto w-full max-w-md"
          >
            {/* Glow halos */}
            <div className="absolute -inset-10 -z-10 rounded-full bg-[radial-gradient(circle,rgba(0,240,255,0.22),transparent_60%)] blur-2xl" />
            <div className="absolute -inset-16 -z-10 rounded-full bg-[radial-gradient(circle,rgba(176,38,255,0.18),transparent_60%)] blur-2xl" />

            {/* Aggiunta la classe 'group' per intercettare l'hover su tutto il blocco del mockup */}
            <div className="animate-float group">
              <div className="relative rounded-[28px] border border-white/10 bg-gradient-to-br from-white/[0.06] to-transparent p-2 backdrop-blur-sm overflow-hidden">
                <div className="overflow-hidden rounded-[22px] border border-white/10">
                  <img
                    src={HERO_MOCKUP}
                    alt="Zero Signals app interface"
                    data-testid="hero-mockup"
                    className="block w-full max-h-[80vh] object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="eager"
                  />
                </div>
              </div>

              {/* Floating badges */}
              <div className="absolute -left-4 top-10 hidden rounded-xl border border-brand-neon-blue/40 bg-black/80 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-brand-neon-blue shadow-[0_0_24px_-6px_rgba(0,240,255,0.7)] backdrop-blur md:block">
                <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-brand-neon-blue" />
                ZeroSignals V1
              </div>
              <div className="absolute -right-2 bottom-12 hidden rounded-xl border border-brand-gold/40 bg-black/80 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-brand-gold shadow-[0_0_24px_-6px_rgba(212,175,55,0.7)] backdrop-blur md:block">
                v2.0.1 · Premium
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}